import HtmlTextWrapper from '@/core/components/html-text-wrapper/html-text-wrapper';
import { IconImage } from '@/core/features';
import { isHex } from '@/core/features/styles/color';
import { hexToRgbaAsCss } from '@/core/utils/color-converter';

export type HintProps = {
    background: {
        alpha: number;
        color: string;
    };
    border?: {
        alpha: number;
        color: string;
        width?: number;
    };
    fontSize?: 'font-size-12' | 'font-size-16' | 'font-size-20';
    highlightBorder?: {
        alpha: number;
        color: string;
        width?: number;
    };
    htmlHintText?: string;
    iconUrl: null | string;
    title: null | string;
};

export default function Hint({
    background,
    border,
    children,
    fontSize = 'font-size-16',
    highlightBorder,
    htmlHintText,
    iconUrl,
    title,
}: React.PropsWithChildren<HintProps>) {
    const backgroundRgba = hexToRgbaAsCss(background.color, background.alpha);
    const highlightBorderRgba = highlightBorder ? hexToRgbaAsCss(highlightBorder.color, highlightBorder.alpha) : '';
    const borderRgba = border ? (isHex(border.color) ? hexToRgbaAsCss(border.color, border.alpha) : border.color) : '';

    const hasHighlightBorder = highlightBorder && backgroundRgba !== highlightBorderRgba && highlightBorder.alpha !== 0;
    const hasBorder = border && backgroundRgba !== borderRgba && border.alpha !== 0;

    return (
        <div
            className={`flex align-center border-radius-5 padding-8 gap-8 break-word ${fontSize}`}
            style={{
                backgroundColor: `${backgroundRgba}`,
                border: hasBorder ? `${border.width ?? 1}px solid ${borderRgba}` : 'none',
                ...(hasHighlightBorder && {
                    borderLeft: `${highlightBorder.width ?? 8}px solid ${highlightBorderRgba}`,
                }),
            }}
        >
            {iconUrl && (
                <IconImage
                    className={'shrink-0'}
                    height={20}
                    url={iconUrl}
                    width={20}
                />
            )}
            <div className={'flex-column gap-8'}>
                {title && <div className={'line-height-22 font-size-18 bold'}>{title}</div>}
                {htmlHintText ? (
                    <HtmlTextWrapper
                        className={'line-height-20'}
                        htmlText={htmlHintText}
                    />
                ) : (
                    children
                )}
            </div>
        </div>
    );
}
